import {Inject, Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {Observable} from 'rxjs';
import {CustomField, CustomFieldPatch} from '@ee/common/models';

@Injectable({providedIn: 'root'})
export class CustomFieldService {
  constructor(private http: HttpClient, @Inject('environment') private environment) {
  }

  getCustomCaseFields(domain: 'case' | 'client'): Observable<CustomField[]> {
    return this.http.get<CustomField[]>(this.environment.api_prefix + `api/organizations/custom-fields/${domain}`);
  }

  upsertCustomCaseFields(domain: 'case' | 'client', field: CustomField): Observable<CustomField> {
    return this.http.post<CustomField>(this.environment.api_prefix + `api/organizations/custom-fields/${domain}`, field);
  }

  /**
   * Edits a definition in place — label, hint, type, size hint — without touching anything else.
   *
   * Deliberately not `upsertCustomCaseFields`: that is a whole-record POST, and the registry panel
   * autosaves one control at a time, so every property the panel does not model would be reset on
   * every keystroke.
   */
  patchCustomField(domain: 'case' | 'client', fieldName: string, patch: CustomFieldPatch): Observable<CustomField> {
    return this.http.patch<CustomField>(
      this.environment.api_prefix + `api/organizations/custom-fields/${domain}/${fieldName}`,
      patch
    );
  }

  deleteCustomField(domain: 'case' | 'client', fieldName: string): Observable<boolean> {
    return this.http.delete<boolean>(this.environment.api_prefix + `api/organizations/custom-fields/${domain}/${fieldName}`);
  }

  // --- Property-manager org-wide custom fields (parallel PM endpoints; PROPERTY_MANAGER role) ---

  getPmCustomCaseFields(domain: 'case' | 'client'): Observable<CustomField[]> {
    return this.http.get<CustomField[]>(this.environment.api_prefix + `api/organizations/pm/custom-fields/${domain}`);
  }

  upsertPmCustomCaseFields(domain: 'case' | 'client', field: CustomField): Observable<CustomField> {
    return this.http.post<CustomField>(this.environment.api_prefix + `api/organizations/pm/custom-fields/${domain}`, field);
  }

  patchPmCustomField(domain: 'case' | 'client', fieldName: string, patch: CustomFieldPatch): Observable<CustomField> {
    return this.http.patch<CustomField>(
      this.environment.api_prefix + `api/organizations/pm/custom-fields/${domain}/${fieldName}`,
      patch
    );
  }

  deletePmCustomField(domain: 'case' | 'client', fieldName: string): Observable<boolean> {
    return this.http.delete<boolean>(
      this.environment.api_prefix + `api/organizations/pm/custom-fields/${domain}/${fieldName}`
    );
  }
}

/**
 * The org-wide custom-field library a host wires into the shared case-template
 * builder (input-driven). The host owns which org-type endpoints back it (attorney
 * `/custom-fields` vs PM `/pm/custom-fields`), keeping the shared builder decoupled.
 */
export interface OrgCustomFieldSource {
  /** All org-wide custom case fields (the shared library). */
  list(): Observable<CustomField[]>;
  /** Define or update an org-wide custom case field. */
  upsert(field: CustomField): Observable<CustomField>;
}
